import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { Draggable } from "react-beautiful-dnd";
import classNames from "classnames";

import {
  makeStyles,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton
} from "@material-ui/core";
import { Cancel, CheckCircle } from "@material-ui/icons";
import blue from "@material-ui/core/colors/blue";
import green from "@material-ui/core/colors/green";

import {
  removeCreative,
  selectCreative,
  deselectCreative,
  editCreative
} from "../../../actions/app/creatives";

import defaultImagePath from "../../../utils/defaultImages/defaultCreative.jpg";
import HorizontalAssetBackupPreview from "./HorizontalAssetBackupPreview";
import { ROTATION_TYPES, WEIGHT_OPTIONS } from "../../../actions/app/constants";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundSize: "contain"
  },
  card: {
    width: "100%",
    marginBottom: 16,
    position: "relative",
    display: "flex"
  },
  thumb: {
    width: 250,
    margin: 4
  },
  selected: {
    border: `2px solid ${blue[500]}`,
    backgroundColor: blue[50]
  },
  details: {
    textAlign: "left",
    width: "80%",
    paddingRight: "45px"
  },
  icon: {
    position: "absolute",
    right: 0
  },
  name: {
    wordBreak: "break-all"
  },
  fileName: {
    paddingBottom: theme.spacing(1),
    wordBreak: "break-all"
  },
  taxonomy: {
    wordBreak: "break-all",
    paddingBottom: theme.spacing(1),
    fontWeight: 500
  },
  thumbCard: {
    width: 250,
    margin: "1.5%",
    position: "relative"
  },
  pointer: {
    cursor: "pointer"
  }
}));

const HorizontalAssetPreview = ({
  id,
  asset,
  index,
  isClickEnabled,
  isDragDisabled,
  isCardSelected,
  handleSelectedCard,
  isWeightEditable,
  isThumbnailEnabled,
  isWeightEnabled
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const assets = useSelector((state) => state.creatives);
  const rotationType = assets.rotationType;

  const handleClick = () => {
    if (isCardSelected) {
      dispatch(deselectCreative(asset.id));
    } else {
      dispatch(selectCreative(asset.id));
    }
    handleSelectedCard(asset.id);
  };

  const editCreativeWeight = (event) => (asset) => {
    if (event.key === "Enter" || event.type == "blur") {
      let editedCreative = { ...asset, weight: parseInt(event.target.value) };
      dispatch(editCreative(editedCreative));
    }
  };

  const renderRotationData = (index, asset, rotationType) => {
    switch (rotationType) {
      case ROTATION_TYPES.WEIGHTED:
        if (
          assets.weightDistribution === WEIGHT_OPTIONS.CUSTOM &&
          isWeightEditable
        ) {
          return (
            <>
              Weight:{" "}
              <input
                onKeyPress={(e) => editCreativeWeight(e)(asset)}
                onBlur={(e) => editCreativeWeight(e)(asset)}
                defaultValue={asset.weight}
                maxLength="3"
                size="1"
              />
            </>
          );
        } else if (location.pathname.includes("preloader")) {
          return '';
        } else {
          return `Weight: ${asset.weight}`;
        }
      case ROTATION_TYPES.SEQUENTIAL:
        return `Sequence: ${index + 1}`;
      default:
        return;
    }
  };

  const cardStyles = classNames({
    [classes.card]: true,
    [classes.pointer]: isClickEnabled,
    [classes.selected]: isCardSelected && isClickEnabled
  });

  const updateDimensions = () => {
    if (asset.dcmCreative !== undefined) {
      if (
        asset.dcmCreative.type !== undefined &&
        asset.dcmCreative.type.includes("TRACKING")
      ) {
        return `1 x 1`;
      } else {
        return `${asset.dcmCreative.size.width} x ${asset.dcmCreative.size.height}`;
      }
    } else if (asset.dimensions === "N/A") {
      return asset.taxonomy.dimensions
        ? asset.taxonomy.dimensions.split("x").join(" x ")
        : "N/A";
    } else {
      return asset.dimensions;
    }
  };

  let RMTypes = [
    "RICH_MEDIA_DISPLAY_BANNER",
    "RICH_MEDIA_DISPLAY_EXPANDING",
    "RICH_MEDIA_DISPLAY_INTERSTITIAL"
  ];

  const validate = () => {
    if (asset.dcmCreative) {
      if (RMTypes.includes(asset.dcmCreative.type)) return true;
    }

    if (location.pathname.includes("display")) {
      if (asset.taxonomy.name && asset.taxonomy.urlId) return true;
    }

    if (location.pathname.includes("preloader")) {
      if (!asset.file.type.includes("zip")) {
        if (asset.taxonomy.name) return true;
      } else {
        if (asset.taxonomy.name && asset.taxonomy.urlId) return true;
      }
    }

    return false;
  };

  return (
    <Draggable
      draggableId={id}
      index={index}
      isDragDisabled={
        isDragDisabled || rotationType !== ROTATION_TYPES.SEQUENTIAL
      }
    >
      {(provided) => (
        <div {...provided.draggableProps} {...provided.dragHandleProps}>
          <Card
            innerRef={provided.innerRef}
            className={cardStyles}
            onClick={isClickEnabled ? handleClick : null}
          >
            {isThumbnailEnabled && asset.preview ? (
              <CardMedia
                className={classes.thumb}
                image={asset.preview ? asset.preview : ""}
                title={asset.file.name}
                key={asset.id}
                classes={{ root: classes.root }}
              />
            ) : null}
            <div className={classes.details}>
              <CardContent className={classes.content}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="p"
                  className={classes.taxonomy}
                >
                  Creative name:{" "}
                  {asset.taxonomy.name ? `${asset.taxonomy.name}` : "N/A"}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="p"
                  className={classes.fileName}
                >
                  File name:{" "}
                  {asset.file.name
                    ? asset.file.name
                    : asset.name
                    ? asset.name
                    : "N/A"}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="p"
                  className={classes.name}
                >
                  Dimensions: {updateDimensions()}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="p"
                  className={classes.name}
                >
                  {asset.creativeRole === "PRIMARY" && isWeightEnabled
                    ? renderRotationData(index, asset, rotationType)
                    : null}
                </Typography>
              </CardContent>
            </div>
            {validate() ? (
              <IconButton className={classes.icon} disabled={true}>
                <CheckCircle style={{ color: green[500] }} />
              </IconButton>
            ) : (
              <IconButton
                className={classes.icon}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(removeCreative(asset.id));
                }}
              >
                <Cancel filled="true" color="action" />
              </IconButton>
            )}
          </Card>
          {asset.backupId ? (
            <HorizontalAssetBackupPreview
              id={asset.backupId}
              index={index}
              key={`${asset.backupId}-${index}`}
              asset={assets.byId[asset.backupId]}
              isClickEnabled={isClickEnabled}
              isDragDisabled={isDragDisabled}
              isCardSelected={isCardSelected}
              isThumbnailEnabled={isThumbnailEnabled}
            />
          ) : null}
        </div>
      )}
    </Draggable>
  );
};

HorizontalAssetPreview.defaultProps = {
  isCardSelected: false,
  handleSelectedCard: () => {},
  isWeightEditable: false
};

export default HorizontalAssetPreview;
